/**
 * LaunchPrepStep.tsx — Sprint 2.7.4.5 Step 6
 * Wizard Step 5: Launch Prep — schedule confirmation + platform prefs +
 * the final "Complete onboarding" button.
 *
 * Three cards:
 *   1. Competitors  — reuses CompetitorList; marks competitor_chosen if needed
 *   2. Schedule & digest preferences
 *   3. AI platform preferences (sweep_opted_out_platforms)
 */

import { useState, useEffect } from 'react';
import { api, ApiError } from '../../../api';
import type { OnboardingStatus, DomainDetail } from '../../../types';
import { CompetitorList } from '../../../components/CompetitorList';
import { DeliveryRouteChooser } from './DeliveryRouteChooser';
import { SchemaDeliveryCard } from '../../dashboard/SchemaDeliveryCard';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  domain: string;
  tier: string;
  status: OnboardingStatus;
  onStatusUpdate: (next: OnboardingStatus) => void;
  onContinue: () => void;
  onSkip: () => void;
  /** Called after POST /onboarding/complete returns 200 */
  onComplete: () => void;
}

interface PlatformInfo {
  id: string;
  name: string;
  description: string;
  chinaJurisdiction?: boolean;
}

// ── Platform metadata ─────────────────────────────────────────────────────────

const PLATFORM_INFO: Record<string, PlatformInfo> = {
  chatgpt:              { id: 'chatgpt',              name: 'ChatGPT',              description: "OpenAI's conversational AI — the most widely used LLM." },
  google_ai_overviews:  { id: 'google_ai_overviews',  name: 'Google AI Overviews',  description: 'AI-generated summaries at the top of Google search results.' },
  claude:               { id: 'claude',               name: 'Claude',               description: "Anthropic's AI assistant, increasingly used for research queries." },
  perplexity:           { id: 'perplexity',           name: 'Perplexity',           description: 'AI-powered search and answer engine with growing enterprise adoption.' },
  gemini:               { id: 'gemini',               name: 'Gemini',               description: "Google's Gemini model, powering Google One AI Premium." },
  deepseek:             { id: 'deepseek',             name: 'DeepSeek',             description: 'High-performance Chinese LLM.', chinaJurisdiction: true },
  mistral:              { id: 'mistral',              name: 'Mistral',              description: 'EU-based AI model (France). Strong general knowledge.' },
};

const TIER_PLATFORMS: Record<string, string[]> = {
  free:    ['chatgpt', 'google_ai_overviews', 'claude'],
  starter: ['chatgpt', 'google_ai_overviews', 'claude'],
  growth:  ['chatgpt', 'google_ai_overviews', 'claude', 'perplexity', 'gemini', 'mistral'],
  scale:   ['chatgpt', 'google_ai_overviews', 'claude', 'perplexity', 'gemini', 'mistral'],
};

// ── Schedule cadence per tier ─────────────────────────────────────────────────

const TIER_CADENCE: Record<string, string> = {
  free:    'N/A — run a one-time audit from the dashboard',
  starter: "Monthly — we'll re-audit your site once a month automatically.",
  growth:  "Change-triggered weekly — re-audits fire whenever we detect meaningful content changes, or weekly at minimum.",
  scale:   "Daily — continuous monitoring with daily re-audits.",
};

const TIER_TRACKED_LIMITS: Record<string, number> = {
  free:    0,
  starter: 5,
  growth:  15,
  scale:   50,
};

// ── Component ─────────────────────────────────────────────────────────────────

export function LaunchPrepStep({
  domainId,
  tier,
  status,
  onStatusUpdate,
  onContinue,
  onSkip,
  onComplete,
}: Props) {
  // ── Domain detail (digest + sweep prefs) ─────────────────────────────────
  const [domainDetail, setDomainDetail] = useState<DomainDetail | null>(null);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [optedOutPlatforms, setOptedOutPlatforms] = useState<Set<string>>(new Set());

  // ── Preferences save state ────────────────────────────────────────────────
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [prefsError, setPrefsError] = useState('');

  // ── Competitors state ─────────────────────────────────────────────────────
  const [competitorChosen, setCompetitorChosen] = useState(
    status.progress.competitor_chosen === true,
  );

  // ── Complete onboarding state ─────────────────────────────────────────────
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string[]>([]);

  // ── Timezone (display only) ───────────────────────────────────────────────
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── Fetch domain detail on mount ──────────────────────────────────────────
  useEffect(() => {
    api.domains.get(domainId)
      .then((d) => {
        setDomainDetail(d);
        setDigestEnabled(d.digestEmailsEnabled ?? true);
        setOptedOutPlatforms(new Set(d.sweepOptedOutPlatforms ?? []));
      })
      .catch(() => null);
  }, [domainId]);

  // ── Platform toggle ───────────────────────────────────────────────────────
  function togglePlatform(platformId: string) {
    setOptedOutPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platformId)) next.delete(platformId);
      else next.add(platformId);
      return next;
    });
    setPrefsSaved(false);
  }

  // ── Save preferences ──────────────────────────────────────────────────────
  async function handleSavePreferences() {
    setPrefsSaving(true);
    setPrefsSaved(false);
    setPrefsError('');
    try {
      await api.domains.setPreferences(domainId, {
        digestEmailsEnabled: digestEnabled,
        sweepOptedOutPlatforms: [...optedOutPlatforms],
      });
      // Mark schedule_confirmed in onboarding progress
      const result = await api.onboarding.patchProgress(domainId, { schedule_confirmed: true });
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      setPrefsSaved(true);
      // Suppress the local competitor state if it's now in the status
      if (result.progress.competitor_chosen) setCompetitorChosen(true);
    } catch {
      setPrefsError('Failed to save preferences. Please try again.');
    } finally {
      setPrefsSaving(false);
    }
  }

  // ── Competitor added callback ─────────────────────────────────────────────
  async function handleCompetitorAdded() {
    if (competitorChosen) return;
    try {
      await api.onboarding.patchProgress(domainId, { competitor_chosen: true });
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      setCompetitorChosen(true);
    } catch {
      // non-fatal
    }
  }

  // ── Complete onboarding ───────────────────────────────────────────────────
  async function handleComplete() {
    setCompleting(true);
    setCompleteError([]);
    try {
      await api.onboarding.complete(domainId);
      onComplete();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 422) {
        const d = err.data as Record<string, unknown>;
        const reasons = Array.isArray(d['gateReasons']) ? (d['gateReasons'] as string[]) : [];
        setCompleteError(
          reasons.length > 0
            ? reasons
            : ['Some steps are incomplete — please review each section and try again.'],
        );
      } else {
        setCompleteError(['Failed to complete onboarding. Please try again.']);
      }
    } finally {
      setCompleting(false);
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const availablePlatforms = TIER_PLATFORMS[tier] ?? TIER_PLATFORMS['starter']!;
  const trackedLimit       = TIER_TRACKED_LIMITS[tier] ?? 5;
  const cadence            = TIER_CADENCE[tier] ?? TIER_CADENCE['starter']!;
  const scheduleConfirmed  = status.progress.schedule_confirmed === true || prefsSaved;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="launch-prep-step">
      <div>
        <h2 className="text-xl font-semibold text-brand-ink">Launch Prep</h2>
        <p className="text-sm text-brand-muted mt-1">
          Confirm your setup and complete onboarding to start your first audit.
        </p>
      </div>

      {/* ── Card 1: Competitors ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="launch-competitors-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-brand-ink text-sm">Competitors</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              Confirm your competitor list before we start scanning.
            </p>
          </div>
          {(status.progress.competitor_chosen || competitorChosen) && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">✓ Saved</span>
          )}
        </div>

        <CompetitorList
          ownedDomainId={domainId}
          availableSlots={trackedLimit}
          onCompetitorAdded={() => void handleCompetitorAdded()}
          emptyStateMessage="We haven't suggested competitors yet. Once your verification and business profile are complete, we'll generate a list automatically. You can also add some manually here."
        />
      </div>

      {/* ── Card 2: Schedule & digest ────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="schedule-card">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-brand-ink text-sm">Schedule & notifications</h3>
          {scheduleConfirmed && (
            <span className="text-xs text-brand-green font-medium bg-green-50 px-2 py-0.5 rounded-full">✓ Saved</span>
          )}
        </div>

        {/* Audit cadence (read-only) */}
        <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
          <p className="text-xs text-brand-muted uppercase tracking-wide mb-1">Audit cadence</p>
          <p className="text-sm text-brand-ink">{cadence}</p>
          <p className="text-xs text-brand-muted mt-1">
            We'll automatically re-audit your site at this cadence; you can also trigger an audit
            anytime from the dashboard.
          </p>
        </div>

        {/* Timezone (display only) */}
        <div>
          <label className="block text-xs text-brand-muted mb-1" htmlFor="detected-timezone">
            Detected timezone
          </label>
          <p
            id="detected-timezone"
            className="text-sm text-brand-ink bg-gray-50 rounded-lg px-3 py-2 border border-gray-100"
            data-testid="detected-timezone"
          >
            {detectedTimezone}
          </p>
          <p className="text-xs text-brand-muted mt-1">Used to schedule notification emails. Change in Settings if needed.</p>
        </div>

        {/* Digest email toggle */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-brand-ink">Competitor digest emails</p>
            <p className="text-xs text-brand-muted mt-0.5">
              We email you when a tracked competitor's score changes meaningfully or new GEO
              citations appear.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={digestEnabled}
            data-testid="digest-toggle"
            onClick={() => { setDigestEnabled((v) => !v); setPrefsSaved(false); }}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none ${
              digestEnabled ? 'bg-brand-green' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform ${
                digestEnabled ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* ── Card 3: AI platform preferences ─────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4" data-testid="platforms-card">
        <div>
          <h3 className="font-medium text-brand-ink text-sm">GEO scan platforms</h3>
          <p className="text-xs text-brand-muted mt-0.5">
            fixmyseo checks your citation presence on these platforms.
            Disable any you want excluded from your reports.
          </p>
        </div>

        <ul className="space-y-2">
          {availablePlatforms.map((pid) => {
            const info = PLATFORM_INFO[pid];
            if (!info) return null;
            const isEnabled = !optedOutPlatforms.has(pid);
            return (
              <li
                key={pid}
                className="flex items-start justify-between gap-4 rounded-xl border border-gray-100 px-4 py-3"
                data-testid={`platform-row-${pid}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-brand-ink">{info.name}</span>
                    {info.chinaJurisdiction && (
                      <span
                        className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200"
                        data-testid={`platform-china-badge-${pid}`}
                      >
                        China jurisdiction
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-brand-muted mt-0.5">{info.description}</p>
                  {info.chinaJurisdiction && (
                    <p className="text-xs text-amber-700 mt-1">
                      Your query data is sent to servers in China. Disable if your business requires
                      data-residency compliance.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isEnabled}
                  data-testid={`platform-toggle-${pid}`}
                  onClick={() => togglePlatform(pid)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                    isEnabled ? 'bg-brand-green' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform ${
                      isEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>

        {prefsError && <p className="text-xs text-red-500">{prefsError}</p>}

        <button
          type="button"
          data-testid="save-prefs-btn"
          disabled={prefsSaving}
          onClick={() => void handleSavePreferences()}
          className="px-4 py-2 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-50"
        >
          {prefsSaving ? 'Saving…' : scheduleConfirmed ? 'Update preferences' : 'Save preferences'}
        </button>
      </div>

      {/* ── Card 4: Schema delivery ──────────────────────────────────────── */}
      <DeliveryRouteChooser
        domainId={domainId}
        initialMode={domainDetail?.schemaDeliveryMode ?? 'undecided'}
      />
      <SchemaDeliveryCard domainId={domainId} />

      {/* ── Complete onboarding ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="complete-card">
        <div>
          <h3 className="font-medium text-brand-ink text-sm">Complete onboarding</h3>
          <p className="text-xs text-brand-muted mt-0.5">
            Once you're ready, click below. We'll queue your first full audit and redirect you to the
            dashboard.
          </p>
        </div>

        {completeError.length > 0 && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3" data-testid="complete-errors">
            <p className="text-xs font-medium text-red-700 mb-1">A few things need attention first:</p>
            <ul className="list-disc list-inside space-y-0.5">
              {completeError.map((r, i) => (
                <li key={i} className="text-xs text-red-700">{r}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="complete-btn"
            disabled={completing}
            onClick={() => void handleComplete()}
            className="px-5 py-2.5 bg-brand-green text-white rounded-lg text-sm font-semibold hover:bg-brand-green/90 disabled:opacity-50"
          >
            {completing ? 'Setting up…' : "You're set up! Complete onboarding →"}
          </button>
          <button
            type="button"
            data-testid="skip-launch-btn"
            onClick={onSkip}
            className="text-sm text-brand-muted hover:text-brand-ink"
          >
            I'll come back to this
          </button>
        </div>
      </div>

      {/* ── Back navigation ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 pt-1">
        <button
          type="button"
          data-testid="continue-btn"
          onClick={onContinue}
          className="text-sm text-brand-muted hover:text-brand-ink"
        >
          ← Back to step 4
        </button>
      </div>
    </div>
  );
}
